import { type FC, useMemo } from 'react';

import { RenderingProvider, useCms } from '@eneco/cms';
import type { HeroRendering } from '@sitecore/types/manual/Hero.types';

import HeroInner from './HeroInner';

/**
 * Bridge between the legacy `<SitecoreContext>`-rendered registry and the
 * new `@eneco/cms` runtime.
 *
 * The legacy `<Placeholder>` calls this component with the JSS-shaped
 * `{ fields, params, placeholders }` props. We convert that single rendering
 * into a `CmsRendering` via the adapter, push it into the `@eneco/cms`
 * `RenderingContext`, and let `HeroInner` read it via `useContent` /
 * `useParams` / `useImageMapper`.
 *
 * Nested placeholder slots (`jss-hero-left`, etc.) inside `HeroInner` still
 * use the legacy `<Placeholder>` from `@sitecore/common`, because the
 * children of those slots are not migrated yet. The legacy
 * `RenderingContext` is set by the outer legacy `<Placeholder>` that
 * mounted us, so those children still find their data normally.
 */
const Hero: FC<HeroRendering> = ({ fields, params, placeholders }) => {
  const { adapter } = useCms();
  const rendering = useMemo(
    () =>
      adapter.toRendering({
        componentName: 'Hero',
        fields,
        params,
        placeholders,
      }),
    [adapter, fields, params, placeholders],
  );

  const hasLeftPlaceholder = !!placeholders?.['jss-hero-left']?.length;
  const hasRightPlaceholder = !!placeholders?.['jss-hero-right']?.length;
  const hasBottomPlaceholder = !!placeholders?.['jss-hero-bottom']?.length;

  return (
    <RenderingProvider rendering={rendering}>
      <HeroInner
        hasLeftPlaceholder={hasLeftPlaceholder}
        hasRightPlaceholder={hasRightPlaceholder}
        hasBottomPlaceholder={hasBottomPlaceholder}
      />
    </RenderingProvider>
  );
};

export { Hero };
export default Hero;
